import { ApolloClient, gql } from '@apollo/client';
import { CollectionOrderStates } from 'shared-pages/shared/constants';

/** Αίτηση σε κατάσταση «Παραληφθείσα από Οδηγό» προς σύνδεση με συλλογή */
export type MatchableCollectionOrder = {
  id: number;
  code?: string | null;
  finDispNote?: string | null;
  collectionPointId: number;
  collectionId?: number | null;
  statusId: number;
};

export type OrderMatchResult =
  | { kind: 'linked'; order: MatchableCollectionOrder }
  | { kind: 'none' }
  | {
      kind: 'mismatch';
      order: MatchableCollectionOrder;
      reason: string;
    };

/**
 * Κανονικοποίηση αριθμού παραστατικού / ΔΑ για σύγκριση:
 * αφαιρεί όλα τα κενά (π.χ. «ΑΔΠ-Δ 12345» ≡ «ΑΔΠ-Δ12345»).
 */
export function normalizeDispNoteKey(value: unknown): string {
  if (value == null) return '';
  return String(value).replace(/\s+/g, '').toUpperCase();
}

const gqlGetOrdersForImportMatch = gql`
  query getCollectionOrdersForImportMatch($where: String, $limit: Int) {
    getCollectionOrders(where: $where, limit: $limit, order: "id DESC") {
      data {
        id
        code
        finDispNote
        collectionPointId
        collectionId
        statusId
      }
      recordCount
    }
  }
`;

/**
 * Φορτώνει αιτήσεις «Παραληφθείσα από Οδηγό» χωρίς συνδεδεμένη συλλογή,
 * για τον συλλέκτη της εισαγωγής (μία φορά ανά import).
 */
export async function loadDeclaredOrdersForImport(
  client: ApolloClient<object>,
  collectorId: number,
): Promise<MatchableCollectionOrder[]> {
  const where = {
    collectorId,
    statusId: CollectionOrderStates.CollectedByDriver,
    collectionId: { $eq: null },
    isActive: true,
  };

  const ret = await client.query({
    query: gqlGetOrdersForImportMatch,
    variables: { where: JSON.stringify(where), limit: 5000 },
    fetchPolicy: 'network-only',
  });

  const data = ret?.data?.getCollectionOrders?.data;
  return Array.isArray(data) ? data : [];
}

/**
 * Βρίσκει αίτηση με ίδιο ΔΑ (χωρίς κενά) και ίδιο σημείο συλλογής.
 * Αν το ΔΑ ταιριάζει αλλά το σημείο όχι → mismatch (δικλίδα ασφαλείας).
 */
export function matchDeclaredOrder(
  orders: MatchableCollectionOrder[],
  excelDispNote: string,
  excelCollectionPointId: number,
): OrderMatchResult {
  const key = normalizeDispNoteKey(excelDispNote);
  if (!key) return { kind: 'none' };

  const byNote = orders.filter((o) => normalizeDispNoteKey(o.finDispNote) === key);
  if (!byNote.length) return { kind: 'none' };

  const withSamePoint = byNote.find((o) => o.collectionPointId === excelCollectionPointId);
  if (withSamePoint) {
    return { kind: 'linked', order: withSamePoint };
  }

  const other = byNote[0];
  return {
    kind: 'mismatch',
    order: other,
    reason: `Το παραστατικό "${excelDispNote}" αντιστοιχεί στην αίτηση ${
      other.code ?? other.id
    }, αλλά το σημείο συλλογής διαφέρει (αίτηση: ${other.collectionPointId}, Excel: ${excelCollectionPointId}).`,
  };
}
